import { Suspense, lazy, useEffect, useRef, useState, type ComponentType, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { RequireSession } from '@/shared/auth';
import { useBrand } from '@/shared/brand/useBrand';
import { BrandLogo } from '@/shared/components/BrandLogo';
import { RouteFallback } from '@/shared/components/RouteFallback';
import { Icon } from '@/shared/components/ui/Icon';
import { TopBar } from '@/shared/components/TopBar';
import { EditorProvider } from '@/shared/editing/EditorContext';
import { useI18n } from '@/shared/i18n/useT';
import { modulesFor } from '@/shared/nav/modules';
import { useOrg } from '@/shared/org';
import { Home } from './Home';
import { HelpPage } from './HelpPage';
import { LibraryDocumentPage } from './library/LibraryDocumentPage';
import { SidebarDocuments } from './SidebarDocuments';
import { NotFound } from './NotFound';
import { OrgSlugEntry } from './OrgSlugEntry';
import { SettingsPage } from './SettingsPage';
import { SignInPage } from './SignInPage';
import { aliasTarget, ROUTE_ALIAS_KEYS } from '@/shared/routing/routeAliases';
import { InviteAcceptancePage } from './InviteAcceptancePage';
import { LegalPage, CancellationPage, isLegalPath } from '@/shared/legal';
import { AiDocsPage, isDocPath } from '@/shared/docs';
import { PublicSheetRoute } from '@/shared/components/PublicSheetRoute';
import { useKeyboardTyping } from '@/shared/hooks/useKeyboardTyping';

const PublicSheetFormRoute = lazy(() =>
  import('@/shared/components/views').then((m) => ({ default: m.PublicSheetFormRoute })),
);
const FormsModule = lazy(() => import('@/modules/forms'));
const TripsModule = lazy(() => import('@/modules/trips'));
const InvestmentsModule = lazy(() => import('@/modules/investments'));
const CashflowModule = lazy(() => import('@/modules/cashflow'));
const CalendarModule = lazy(() => import('@/modules/calendar'));
const NotesModule = lazy(() => import('@/modules/notes'));
// The module directory itself: reached from the drawer and from the Apps tile
// on Home, but not part of the initial paint of either.
const AppsPage = lazy(() => import('./AppsPage'));
const AiPage = lazy(() => import('./AiPage'));
const OAuthConsentPage = lazy(() => import('./OAuthConsentPage'));
const PlansPage = lazy(() => import('@/shared/billing/UpgradeOffer'));
const ModulePlanGate = lazy(() => import('@/shared/billing/UpgradeOffer').then(module => ({ default: module.ModulePlanGate })));
const PollPage = lazy(() => import('@/client/PollPage').then(m => ({ default: m.PollPage })));

/**
 * numtable.com — the professional's shell. Full module suite.
 * Its counterpart is ClientShell (procalendar.app); they share no UI.
 */
export function AppShell() {
  useKeyboardTyping();
  const { t } = useI18n();
  const brand = useBrand();
  const location = useLocation();
  const org = useOrg();
  const [isPinned, setIsPinned] = useState(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(min-width: 640px)').matches) {
      return localStorage.getItem('numtable_sidebar_pinned') === 'true';
    }
    return false;
  });
  const [menuOpen, setMenuOpen] = useState(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(min-width: 640px)').matches) {
      return localStorage.getItem('numtable_sidebar_open') === 'true';
    }
    return false;
  });

  useEffect(() => {
    if (window.matchMedia('(min-width: 640px)').matches) {
      localStorage.setItem('numtable_sidebar_open', String(menuOpen));
      localStorage.setItem('numtable_sidebar_pinned', String(isPinned));
    }
  }, [menuOpen, isPinned]);

  const menuPanelRef = useRef<HTMLElement>(null);       // mobile drawer
  const desktopMenuPanelRef = useRef<HTMLElement>(null); // desktop aside
  const menuCloseRef = useRef<HTMLButtonElement>(null);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  // Hover management: debounce close so mouse can travel from trigger → panel
  const hoverCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const publicSheet = org.status === 'signedOut' && /^\/sheets\/[^/]+$/.test(location.pathname);
  const publicSheetForm =
    /^\/sheets\/[^/]+\/form(?:\/[^/]+)?$/.test(location.pathname) ||
    /^\/form\/[^/]+$/.test(location.pathname);
  const publicModuleDocument = org.status === 'signedOut' && /^(?:\/investments|\/notes|\/forms|\/calendar)\/[^/]+$/.test(location.pathname);
  const publicAuth = location.pathname === '/signin' || location.pathname.startsWith('/invite/') || location.pathname === '/oauth/consent';
  const publicLegal = isLegalPath(location.pathname);
  const publicDocs = isDocPath(location.pathname);
  const publicPoll = location.pathname.startsWith('/poll');
  const hideMemberChrome = publicSheet || publicSheetForm || publicModuleDocument || publicAuth || publicLegal || publicDocs || publicPoll;
  const documentTopBar = /^\/(?:sheets|trips|investments|notes|forms|cashflow)\/[^/]+(?:\/|$)/.test(location.pathname) && !publicSheetForm;

  // The back-office is in the menu only from inside the platform organization.
  // Switching organization changes the menu, which is the same rule the module
  // itself and the Worker both apply.
  const modules = modulesFor(
    org.status === 'ready' && org.membership.organization.isPlatform,
  );
  // A route change closes unpinned desktop sidebar or mobile drawer.
  useEffect(() => {
    if (window.matchMedia('(max-width: 639px)').matches || !isPinned) {
      setMenuOpen(false);
    }
  }, [location.pathname, isPinned]);

  // Lock background scroll only on mobile when the drawer modal is open.
  useEffect(() => {
    if (!menuOpen) return;
    if (window.matchMedia('(min-width: 640px)').matches) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    // On mobile, focus close button.
    if (window.matchMedia('(max-width: 639px)').matches) {
      menuCloseRef.current?.focus();
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && window.matchMedia('(max-width: 639px)').matches) {
        e.preventDefault();
        setMenuOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const trapMenuFocus = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Tab' || window.matchMedia('(min-width: 640px)').matches) return;
    const focusable = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('a[href],button:not([disabled])')).filter((element) => element.getClientRects().length > 0);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    if (!event.currentTarget.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  useEffect(() => {
    if (!menuOpen) return;
    const handleOutsideClick = (e: MouseEvent | PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (
        menuPanelRef.current?.contains(target) ||
        desktopMenuPanelRef.current?.contains(target) ||
        menuTriggerRef.current?.contains(target)
      ) {
        return;
      }
      if (isPinned && window.matchMedia('(min-width: 640px)').matches) {
        return;
      }
      setMenuOpen(false);
    };
    document.addEventListener('pointerdown', handleOutsideClick);
    return () => {
      document.removeEventListener('pointerdown', handleOutsideClick);
    };
  }, [menuOpen, isPinned]);

  const isHome = location.pathname === '/';

  // Desktop hover: open sidebar when mouse enters the trigger button
  const handleTriggerMouseEnter = () => {
    if (window.matchMedia('(max-width: 639px)').matches) return;
    if (hoverCloseTimer.current) { clearTimeout(hoverCloseTimer.current); hoverCloseTimer.current = null; }
    if (!isPinned) setMenuOpen(true);
  };

  // Cancel a pending hover-close when mouse enters the sidebar panel
  const handlePanelMouseEnter = () => {
    if (hoverCloseTimer.current) { clearTimeout(hoverCloseTimer.current); hoverCloseTimer.current = null; }
  };

  // Close sidebar after a short delay on mouse-leave (unless pinned)
  const handleHoverMouseLeave = () => {
    if (window.matchMedia('(max-width: 639px)').matches || isPinned) return;
    hoverCloseTimer.current = setTimeout(() => {
      setMenuOpen(false);
    }, 120);
  };

  const handleNavOptionClick = () => {
    // Mobile: always close. Desktop: close only when in hover mode (not pinned).
    if (window.matchMedia('(max-width: 639px)').matches || !isPinned) {
      setMenuOpen(false);
    }
  };

  const navContent = (
    <div onClick={handleNavOptionClick} className="flex flex-col gap-1">
      <button
        type="button"
        ref={menuCloseRef}
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen(false);
          setIsPinned(false);
        }}
        aria-label={t('nav.closeMenu')}
        className="tap-target mb-2 flex w-full items-center justify-end rounded-full px-3 text-muted hover:bg-surface-3 hover:text-fg sm:hidden"
      >
        <Icon name="close" className="h-5 w-5" />
      </button>
      <NavLink to="/" end className={drawerLink}>
        <Icon name="home" />
        <span className="truncate">{t('nav.home')}</span>
      </NavLink>
      <SidebarDocuments open={menuOpen} linkClassName={drawerLink} />
    </div>
  );

  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('numtable_sidebar_width');
    const parsed = saved ? parseInt(saved, 10) : 240;
    return Number.isFinite(parsed) && parsed >= 180 && parsed <= 600 ? parsed : 240;
  });

  const resizeDrag = useRef<{ x0: number; w0: number } | null>(null);

  const startSidebarResize = (e: React.PointerEvent) => {
    e.preventDefault();
    resizeDrag.current = { x0: e.clientX, w0: sidebarWidth };

    const onPointerMove = (event: PointerEvent) => {
      if (!resizeDrag.current) return;
      const delta = event.clientX - resizeDrag.current.x0;
      const next = Math.max(180, Math.min(600, resizeDrag.current.w0 + delta));
      setSidebarWidth(next);
      localStorage.setItem('numtable_sidebar_width', String(next));
    };

    const onPointerUp = () => {
      resizeDrag.current = null;
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  useEffect(() => {
    localStorage.setItem('numtable_sidebar_width', String(sidebarWidth));
  }, [sidebarWidth]);

  return (
    <EditorProvider key={location.pathname}>
      <div className="min-h-dvh overflow-x-clip bg-surface text-fg">
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded-full focus:bg-accent focus:px-3 focus:py-2 focus:text-accent-fg"
        >
          {t('common.skipToContent')}
        </a>

        <header data-hide-while-typing className={hideMemberChrome ? 'hidden' : 'sticky top-0 z-50 border-b border-header-border bg-header-bg text-header-fg shadow-sm'}>
          <div
            className={`flex h-header w-full items-center ${documentTopBar ? 'gap-1 sm:gap-3' : 'gap-3'} px-3 sm:px-6`}
            style={{ paddingTop: 'env(safe-area-inset-top)' }}
          >
            {/* Mobile back arrow: navigates back to home, hidden on home page itself */}
            {!isHome && (
              <NavLink
                to="/"
                aria-label={t('nav.backToHome')}
                className="tap-target -ml-2 flex items-center justify-center rounded-full text-header-muted hover:text-header-fg sm:hidden"
              >
                <Icon name="arrowLeft" className="h-6 w-6" />
              </NavLink>
            )}

            {/* Desktop sidebar toggle.
                - Hover: opens temporarily; mouse-leave or option-click closes it.
                - Click: locks sidebar open; only a second click on this button closes it. */}
            <button
              type="button"
              ref={menuTriggerRef}
              onClick={() => {
                if (isPinned) {
                  // Unpin and close
                  setIsPinned(false);
                  setMenuOpen(false);
                } else {
                  // Pin open (cancel any pending hover-close first)
                  if (hoverCloseTimer.current) { clearTimeout(hoverCloseTimer.current); hoverCloseTimer.current = null; }
                  setIsPinned(true);
                  setMenuOpen(true);
                }
              }}
              onMouseEnter={handleTriggerMouseEnter}
              onMouseLeave={handleHoverMouseLeave}
              aria-expanded={menuOpen}
              aria-controls="app-nav"
              aria-label={isPinned ? t('nav.closeMenu') : t('nav.toggleSidebar')}
              title={isPinned ? t('nav.closeMenu') : t('nav.toggleSidebar')}
              className={`tap-target ${isHome ? '-ml-2 sm:-ml-5' : '-ml-5'} hidden nt-icon-trigger nt-icon-trigger--header text-header-muted hover:text-header-fg sm:inline-flex`}
            >
              <Icon name="sidebar" className="h-5 w-5" />
            </button>

            <NavLink
              to="/"
              className={`${documentTopBar ? 'hidden sm:flex' : 'flex'} min-h-11 shrink-0 items-center gap-2 rounded-full`}
              aria-label={brand.name}
            >
              {isHome ? (
                <>
                  <div className="flex items-center gap-2 sm:hidden">
                    <BrandLogo
                      tone="dark"
                      variant="icon"
                      className="h-6 w-auto"
                    />
                    <span className="text-lg font-medium text-header-fg">
                      {brand.name.toLowerCase()}
                    </span>
                  </div>
                  <BrandLogo
                    tone="dark"
                    variant="wordmark"
                    className="hidden h-6 w-auto sm:block sm:h-7"
                  />
                </>
              ) : (
                <BrandLogo
                  tone="dark"
                  variant="responsive"
                  wordmarkBreakpoint="sm"
                  className="h-6 w-auto sm:h-7"
                />
              )}
            </NavLink>

            <TopBar documentLayout={documentTopBar} />
          </div>
        </header>

        {/* Main app body layout (with desktop sidebar support) */}
        <div data-app-body className="relative flex h-[calc(100dvh-var(--header-h,48px)-1px)] w-full">
          {/* Desktop Left Sidebar:
              - Pinned (click): in-flow, pushes content to the right.
              - Hover (not pinned): absolute overlay, content stays put. */}
          {menuOpen && !hideMemberChrome && (
            <>
              <aside
                id="app-nav"
                ref={desktopMenuPanelRef}
                aria-label={t('nav.primary')}
                style={{ width: `${sidebarWidth}px` }}
                className={`hidden shrink-0 flex-col gap-1 overflow-y-auto border-r border-border bg-surface p-3 sm:flex select-none ${
                  isPinned
                    ? 'relative'
                    : 'absolute inset-y-0 left-0 z-50 shadow-lg'
                }`}
                onMouseEnter={handlePanelMouseEnter}
                onMouseLeave={handleHoverMouseLeave}
              >
                {navContent}
              </aside>
              {/* Draggable resize handle — sibling of aside so it sits to the
                  right of the scrollbar, not behind it. Only shown in pinned mode. */}
              {isPinned && (
                <div
                  onPointerDown={startSidebarResize}
                  className="relative hidden sm:block w-1.5 shrink-0 cursor-col-resize hover:bg-accent/40 active:bg-accent transition-colors"
                  title={t('grid.dragResize')}
                />
              )}
            </>
          )}

          {/* Mobile drawer: modal overlay on small screens */}
          {menuOpen && !hideMemberChrome && (
            <div className="fixed inset-0 z-50 sm:hidden">
              <button
                type="button"
                aria-label={t('nav.dismissMenu')}
                onClick={() => setMenuOpen(false)}
                className="absolute inset-0 bg-[var(--overlay)]"
              />
              <nav
                ref={menuPanelRef}
                onKeyDown={trapMenuFocus}
                role="dialog"
                aria-modal="true"
                aria-label={t('nav.primary')}
                className="absolute inset-y-0 left-0 flex w-[min(20rem,85vw)] flex-col gap-1 overflow-y-auto border-r border-border bg-surface p-3"
                style={{
                  paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)',
                  paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)',
                }}
              >
                {navContent}
              </nav>
            </div>
          )}

          <main id="main" className={`min-w-0 flex-1 overflow-y-auto ${publicSheetForm ? 'p-0' : 'px-3 sm:px-6'}`}>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* Not in the nav: you land here from a guard or a signed-out action. */}
                <Route path="signin" element={<SignInPage />} />
                <Route path="invite/:token" element={<InviteAcceptancePage />} />
                <Route path="oauth/consent" element={<OAuthConsentPage />} />
                <Route path="privacy" element={<LegalPage document="privacy" />} />
                <Route path="terms" element={<LegalPage document="terms" />} />
                <Route path="baja" element={<CancellationPage />} />
                <Route path="docs/ai" element={<AiDocsPage />} />
                <Route path="poll/:pollId" element={<PollPage />} />
                <Route path="poll" element={<PollPage />} />
                <Route path="sheets/:documentId/form" element={<PublicSheetFormRoute />} />
                <Route path="sheets/:documentId/form/:tabId" element={<PublicSheetFormRoute />} />
                <Route path="form/:formId" element={<PublicSheetFormRoute />} />
                <Route path="sheets/:documentId" element={<PublicSheetRoute />} />
                <Route path="trips/*" element={<DeepModuleRoute Component={TripsModule} />} />
                <Route path="investments/:documentId/*" element={<DeepModuleRoute Component={InvestmentsModule} />} />
                <Route path="notes/:documentId/*" element={<DeepModuleRoute Component={NotesModule} />} />
                <Route path="forms/:documentId/*" element={<DeepModuleRoute Component={FormsModule} />} />
                <Route path="cashflow/:documentId/*" element={<DeepModuleRoute Component={CashflowModule} />} />
                <Route path="calendar/:documentId/*" element={<DeepModuleRoute Component={CalendarModule} />} />
                {ROUTE_ALIAS_KEYS.map((alias) => (
                  <Route key={alias} path={`${alias}/*`} element={<AliasRedirect />} />
                ))}
                <Route
                  path="*"
                  element={
                    <RequireSession>
                      <Routes>
                        <Route index element={<Home />} />
                        <Route path="apps" element={<AppsPage />} />
                        <Route path="ai" element={<AiPage />} />
                        <Route path="settings" element={<SettingsPage />} />
                        <Route path="help" element={<HelpPage />} />
                        <Route path="plans" element={<PlansPage />} />
                        <Route path="drive/*" element={<Navigate replace to="/folders" />} />
                        {/* My files and Trash are Home tabs at /folders addresses, so a
                            folder link can be bookmarked or shared. These static
                            routes outrank the folders module's `folders/*`. */}
                        <Route path="trash" element={<Navigate replace to="/folders/trash" />} />
                        <Route path="starred" element={<Home />} />
                        <Route path="recent" element={<Home />} />
                        <Route path="shared" element={<Home />} />
                        <Route path="library" element={<Home />} />
                        <Route path="library/:documentId/:sheet?" element={<LibraryDocumentPage />} />
                        <Route path="folders" element={<Home />} />
                        <Route path="folders/:nodeId" element={<Home />} />
                        {modules.map(({ id, path, plan, Component }) => (
                          <Route
                            key={id}
                            path={`${path}/*`}
                            element={(plan !== 'public' && plan !== 'free') || id === 'cashflow'
                              ? <ModulePlanGate moduleKey={id}><DeepModuleRoute Component={Component} /></ModulePlanGate>
                              : <DeepModuleRoute Component={Component} />}
                          />
                        ))}
                        {/* Last, and single-segment: everything the router
                            answers itself has already matched above, and the
                            reserved list refuses those names at request time so
                            no organization can ever own one. */}
                        <Route path=":orgSlug" element={<OrgSlugEntry />} />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </RequireSession>
                  }
                />
              </Routes>
            </Suspense>
          </main>
        </div>
      </div>
    </EditorProvider>
  );
}

function DeepModuleRoute({ Component }: { readonly Component: ComponentType }) {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Component />
    </Suspense>
  );
}

function AliasRedirect() {
  const location = useLocation();
  const target = aliasTarget(location.pathname);
  return <Navigate replace to={`${target}${location.search}${location.hash}`} />;
}


const drawerLink = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-3 rounded-full px-3 py-2 text-sm font-medium transition-colors ${
    isActive
      ? 'bg-accent-soft text-accent font-semibold'
      : 'text-muted hover:bg-surface-3 hover:text-fg'
  }`;
